import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ThemeToggle } from '@/components/theme-toggle';
import { 
  Menu, 
  X, 
  ChevronDown, 
  ChevronRight,
  ArrowRight, 
  Globe, 
  Cloud, 
  Building2, 
  Cpu, 
  ShieldCheck, 
  Zap, 
  Layers,
  Newspaper,
  Users,
  ExternalLink,
  MapPin,
  TrendingUp,
  Handshake,
  MessageSquare,
  Calendar,
  FileText,
  PlayCircle,
  Network,
  Search as SearchIcon
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import Search from '@/components/ui/Search';
import { useTheme } from '@/components/theme-provider';
import { useLanguage } from '@/contexts/LanguageContext';

// --- Data Structures ---

type TFn = (pt: string, es: string, en?: string) => string;

const getPartnersMegamenu = (t: TFn) => ({
  groups: [
    {
      id: 'tech',
      title: t('Tecnologia', 'Tecnología', 'Technology'),
      desc: t('Parceiros globais integrados pela TechEnabler.', 'Socios globales integrados por TechEnabler.', 'Global partners integrated by TechEnabler.'),
      icon: Zap,
      items: [
        { name: 'AVSystem', href: '/parceiros/tecnologia#avsystem' },
        { name: 'Corero', href: '/parceiros/tecnologia#corero' },
        { name: 'Kentik', href: '/parceiros/tecnologia#kentik' },
        { name: 'Planisys', href: '/parceiros/tecnologia#planisys' },
        { name: 'Unryo', href: '/parceiros/tecnologia#unryo' },
      ]
    },
    {
      id: 'commercial',
      title: t('Comerciais', 'Comerciales', 'Commercial'),
      desc: t('Presença regional e capilaridade local.', 'Presencia regional y capilaridad local.', 'Regional presence and local reach.'),
      icon: MapPin,
      items: [
        { name: t('Ver parceiros comerciais por país', 'Ver socios comerciales por país', 'See commercial partners by country'), href: '/parceiros/comerciais' },
        { name: 'Argentina', href: '/parceiros/comerciais#argentina' },
        { name: t('Colômbia', 'Colombia', 'Colombia'), href: '/parceiros/comerciais#colombia' },
        { name: t('Equador', 'Ecuador', 'Ecuador'), href: '/parceiros/comerciais#equador' },
        { name: 'Guatemala', href: '/parceiros/comerciais#guatemala' },
        { name: t('Panamá', 'Panamá', 'Panama'), href: '/parceiros/comerciais#panama' },
        { name: 'Peru', href: '/parceiros/comerciais#peru' },
        { name: t('Uruguai', 'Uruguay', 'Uruguay'), href: '/parceiros/comerciais#uruguai' },
      ]
    },
    {
      id: 'business',
      title: t('Clientes e parceiros de negócio', 'Clientes y socios de negocio', 'Customers and business partners'),
      desc: t('Ampliação de alcance e valor agregado.', 'Ampliación de alcance y valor agregado.', 'Extended reach and added value.'),
      icon: Handshake,
      items: [
        { name: t('Visão geral da página', 'Visión general de la página', 'Page overview'), href: '/parceiros/clientes-e-negocios#hero' },
        { name: t('Aplicação OTT e valor agregado', 'Aplicación OTT y valor agregado', 'OTT application and added value'), href: '/parceiros/clientes-e-negocios#aplicacao-mercado' },
        { name: t('Crescimento conjunto', 'Crecimiento conjunto', 'Growing together'), href: '/parceiros/clientes-e-negocios#geracao-valor' },
      ]
    }
  ],
  card: {
    title: t('Ecossistema que amplia capacidade e alcance', 'Ecosistema que amplía capacidad y alcance', 'An ecosystem that expands capacity and reach'),
    desc: t('Tecnologia, presença regional e relações estratégicas fortalecem a entrega da TechEnabler.', 'Tecnología, presencia regional y relaciones estratégicas fortalecen la entrega de TechEnabler.', 'Technology, regional presence and strategic relationships strengthen TechEnabler delivery.'),
    cta: t('Ver todos os parceiros', 'Ver todos los socios', 'See all partners'),
    href: '/parceiros'
  }
});

const getPressMegamenu = (t: TFn) => ({
  groups: [
    {
      id: 'releases',
      title: 'Press releases',
      desc: t('Press releases e anúncios estratégicos.', 'Comunicados de prensa y anuncios estratégicos.', 'Press releases and strategic announcements.'),
      icon: Newspaper,
      items: [
        { name: t('Últimos press releases', 'Últimos comunicados de prensa', 'Latest press releases'), href: '/sala-de-imprensa#press-releases' },
        { name: t('Lançamentos', 'Lanzamientos', 'Launches'), href: '/sala-de-imprensa#press-releases' },
        { name: t('Expansão e parcerias', 'Expansión y asociaciones', 'Expansion and partnerships'), href: '/sala-de-imprensa#press-releases' },
      ]
    },
    {
      id: 'media',
      title: t('Mídia', 'Medios', 'Media'),
      desc: t('Presença editorial e recursos visuais.', 'Presencia editorial y recursos visuales.', 'Editorial presence and visual resources.'),
      icon: PlayCircle,
      items: [
        { name: t('Materiais institucionais', 'Materiales institucionales', 'Corporate materials'), href: '/sala-de-imprensa#midia' },
        { name: 'PDFs', href: '/sala-de-imprensa#midia' },
        { name: t('Contato para imprensa', 'Contacto para prensa', 'Press contact'), href: '/contato' },
      ]
    },
    {
      id: 'cases',
      title: t('Estudos de caso', 'Casos de estudio', 'Case studies'),
      desc: t('Resultados reais em ambientes críticos.', 'Resultados reales en entornos críticos.', 'Real results in critical environments.'),
      icon: TrendingUp,
      items: [
        { name: 'Telecom & ISPs', href: '/sala-de-imprensa#press-releases' },
        { name: 'Enterprise', href: '/sala-de-imprensa#press-releases' },
      ]
    },
    {
      id: 'events',
      title: t('Eventos', 'Eventos', 'Events'),
      desc: t('Onde a TechEnabler está presente.', 'Donde TechEnabler está presente.', 'Where TechEnabler is present.'),
      icon: Calendar,
      items: [
        { name: t('Próximos eventos', 'Próximos eventos', 'Upcoming events'), href: '/sala-de-imprensa#eventos' },
        { name: t('Histórico', 'Histórico', 'Past events'), href: '/sala-de-imprensa#eventos' },
      ]
    }
  ],
  card: {
    title: t('Acompanhe os movimentos mais recentes', 'Siga los últimos movimientos', 'Follow our latest moves'),
    desc: t('Press releases, materiais institucionais e presença editorial em um só lugar.', 'Comunicados de prensa, materiales corporativos y presencia editorial en un solo lugar.', 'Press releases, corporate materials and editorial presence in one place.'),
    cta: t('Ver newsroom completa', 'Ver sala de prensa completa', 'See full newsroom'),
    href: '/sala-de-imprensa'
  }
});

const getNavigation = (t: TFn) => [
  { name: 'Home', href: '/' },
  { name: t('Sobre nós', 'Sobre nosotros', 'About us'), href: '/sobre-nos' },
  { name: t('Soluções', 'Soluciones', 'Solutions'), href: '/solucoes' },
  { name: t('Sala de Imprensa', 'Sala de Prensa', 'Newsroom'), href: '/sala-de-imprensa', megamenu: { type: 'press', data: getPressMegamenu(t) } },
  { name: t('Parceiros', 'Socios', 'Partners'), href: '/parceiros', megamenu: { type: 'partners', data: getPartnersMegamenu(t) } },
  { name: t('Contato', 'Contacto', 'Contact'), href: '/contato' },
];

export default function Header() {
  const { theme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeMegamenu, setActiveMegamenu] = useState<string | null>(null);
  const [activeSubItem, setActiveSubItem] = useState<string | null>(null);
  const location = useLocation();
  const headerRef = useRef<HTMLElement>(null);

  const navigation = getNavigation(t);

  const isBlueHeroPage = location.pathname.startsWith('/solucoes');
  const isInverted = isBlueHeroPage && !scrolled && !activeMegamenu;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setActiveMegamenu(null);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Set default sub-item when megamenu opens
  useEffect(() => {
    if (activeMegamenu) {
      const navItem = navigation.find(n => n.name === activeMegamenu);
      if (navItem?.megamenu) {
        if (navItem.megamenu.type === 'partners') {
          setActiveSubItem(getPartnersMegamenu(t).groups[0].id);
        } else if (navItem.megamenu.type === 'press') {
          setActiveSubItem(getPressMegamenu(t).groups[0].id);
        }
      }
    } else {
      setActiveSubItem(null);
    }
  }, [activeMegamenu, t]);

  return (
    <header 
      ref={headerRef}
      onMouseLeave={() => setActiveMegamenu(null)}
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b h-20 flex items-center",
        scrolled || activeMegamenu 
          ? "bg-white/92 dark:bg-[#0b0d10]/88 backdrop-blur-md border-[#e5e7eb] dark:border-[#262b33]" 
          : "bg-transparent border-transparent"
      )}
    >
      <nav className="container-custom flex items-center justify-between w-full h-full animate-fade-in" aria-label="Global">
        <div className="flex lg:flex-1 h-full items-center min-w-0">
          <Link to="/" className="-m-1.5 p-1.5 flex items-center gap-2 group shrink-0 min-w-0">
            <img
              src="/techenabler-logo-gradiente.webp"
              alt="TechEnabler"
              width={479}
              height={120}
              className={cn(
                "h-[32px] sm:h-[37px] lg:h-[37px] xl:h-[46px] w-auto group-hover:scale-105 transition-all duration-300 translate-y-[-1px] object-contain shrink-0",
                isInverted ? "hidden" : "block dark:hidden"
              )}
              referrerPolicy="no-referrer"
            />
            <img
              src="/techenabler-logo-branco.webp"
              alt=""
              width={479}
              height={120}
              className={cn(
                "h-[32px] sm:h-[37px] lg:h-[37px] xl:h-[46px] w-auto group-hover:scale-105 transition-all duration-300 translate-y-[-1px] object-contain shrink-0",
                isInverted ? "block" : "hidden dark:block"
              )}
              referrerPolicy="no-referrer"
            />
          </Link>
        </div>
        
        <div className="flex lg:hidden">
          <button
            type="button"
            className={cn(
              "-m-2.5 inline-flex items-center justify-center rounded-md p-2.5",
              isInverted ? "text-white" : "text-foreground"
            )}
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Abrir menu</span>
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="hidden lg:flex lg:gap-x-4 xl:gap-x-8 h-full items-center">
          {navigation.map((item) => (
            <div 
              key={item.name} 
              className="h-full flex items-center"
              onMouseEnter={() => item.megamenu && setActiveMegamenu(item.name)}
            >
              <Link
                to={item.href}
                className={cn(
                  "text-[9px] xl:text-[10px] font-bold uppercase tracking-[0.1em] xl:tracking-[0.15em] transition-colors flex items-center gap-1 xl:gap-1.5 h-full relative",
                  isInverted 
                    ? (location.pathname === item.href || activeMegamenu === item.name ? "text-white" : "text-white/70 hover:text-white")
                    : (location.pathname === item.href || activeMegamenu === item.name ? "text-foreground" : "text-muted-foreground hover:text-foreground")
                )}
              >
                {item.name}
                {item.megamenu && (
                  <ChevronDown className={cn(
                    "h-3 w-3 transition-transform duration-200",
                    activeMegamenu === item.name ? "rotate-180" : ""
                  )} />
                )}
                {location.pathname === item.href && (
                  <motion.div 
                    layoutId="nav-active"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                  />
                )}
              </Link>
            </div>
          ))}
        </div>

        <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:items-center lg:gap-3 xl:gap-8">
          <div className="flex items-center gap-1.5 xl:gap-2 border-r border-border pr-3 xl:pr-6 mr-1 xl:mr-2">
            <button
              onClick={() => setLanguage('pt')}
              className={cn(
                "text-lg transition-opacity hover:opacity-100",
                language === 'pt' ? "opacity-100 grayscale-0" : "opacity-40 grayscale"
              )}
              title="Português"
              aria-label="Português"
            >
              🇧🇷
            </button>
            <button
              onClick={() => setLanguage('es')}
              className={cn(
                "text-lg transition-opacity hover:opacity-100",
                language === 'es' ? "opacity-100 grayscale-0" : "opacity-40 grayscale"
              )}
              title="Español"
              aria-label="Español"
            >
              🇪🇸
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={cn(
                "text-lg transition-opacity hover:opacity-100",
                language === 'en' ? "opacity-100 grayscale-0" : "opacity-40 grayscale"
              )}
              title="English"
              aria-label="English"
            >
              🇺🇸
            </button>
          </div>
          <button
            onClick={() => setSearchOpen(true)}
            className={cn(
              "p-2.5 transition-colors focus:outline-none",
              isInverted ? "text-white/70 hover:text-white" : "text-muted-foreground hover:text-primary"
            )}
            aria-label="Pesquisar"
          >
            <SearchIcon className="h-5 w-5" />
          </button>
          <ThemeToggle className={isInverted ? "text-white/70 hover:text-white" : ""} />
        </div>
      </nav>

      {/* Megamenu Desktop - Moved out of the loop for full-width stability */}
      <AnimatePresence>
        {activeMegamenu && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            onMouseEnter={() => setActiveMegamenu(activeMegamenu)}
            onMouseLeave={() => setActiveMegamenu(null)}
            className="absolute top-full left-0 right-0 w-full bg-background/98 backdrop-blur-xl border-b border-border shadow-2xl overflow-y-auto max-h-[95vh] z-50"
          >
            {navigation.find(n => n.name === activeMegamenu)?.megamenu && (
              <div className="container-custom">
                <div className="grid grid-cols-12 gap-0 overflow-hidden min-h-[480px]">
                  
                  {/* 1. Coluna Esquerda - Navegação Principal (MERCADO) */}
                  <div className="col-span-4 border-r border-border p-5 py-8 bg-zinc-50/50 dark:bg-zinc-900/10">
                    <div className="mb-6">
                      <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 mb-5 px-3">
                        {t('Selecione o Mercado', 'Seleccione el Mercado', 'Select a Market')}
                      </h3>
                      <div className="space-y-2">
                        {(navigation.find(n => n.name === activeMegamenu)?.megamenu?.type === 'partners' ? getPartnersMegamenu(t).groups : 
                          getPressMegamenu(t).groups).map((sub: any) => (
                          <button
                            key={sub.id}
                            onMouseEnter={() => setActiveSubItem(sub.id)}
                            className={cn(
                              "w-full text-left p-2.5 rounded-lg transition-all flex items-center gap-3 group/sub",
                              activeSubItem === sub.id ? "bg-card border border-border shadow-sm" : "border border-transparent hover:bg-card/40"
                            )}
                          >
                            <div className={cn(
                              "w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors",
                              activeSubItem === sub.id ? "bg-[#1A237E] text-white" : "bg-primary/10 text-primary group-hover/sub:bg-primary/20"
                            )}>
                              <sub.icon className={cn("h-3.5 w-3.5", activeSubItem === sub.id ? "text-white" : "text-primary")} />
                            </div>
                            <div className="space-y-0.5 flex-1">
                              <h4 className={cn(
                                "text-[10px] font-bold uppercase tracking-[0.12em] transition-colors",
                                activeSubItem === sub.id ? "text-[#1A237E]" : "text-muted-foreground group-hover/sub:text-foreground"
                              )}>
                                {sub.title}
                              </h4>
                              <p className="text-[9px] text-muted-foreground leading-tight line-clamp-1 font-medium tracking-tight">
                                {sub.description || sub.desc}
                              </p>
                            </div>
                            {activeSubItem === sub.id && (
                              <ChevronRight className="h-3.5 w-3.5 text-primary ml-auto shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* 2. Área Central - Conteúdo Contextual (ALCANCE > CATEGORIA > SERVIÇO) */}
                  <div className="col-span-8 p-8 overflow-y-auto bg-background/50">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeSubItem}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                        className="h-full flex flex-col"
                      >
                        {navigation.find(n => n.name === activeMegamenu)?.megamenu?.type === 'partners' && (
                          <div className="h-full flex flex-col pt-10">
                            <div className="grid grid-cols-2 gap-8 flex-1">
                              <div className="space-y-8">
                                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-primary/60 border-l-2 border-primary/20 pl-4">{t('Navegação', 'Navegación', 'Navigation')}</h3>
                                <ul className="space-y-6">
                                  {getPartnersMegamenu(t).groups.find(g => g.id === activeSubItem)?.items.map(p => (
                                    <li key={p.name}>
                                      {p.href.startsWith('http') ? (
                                        <a 
                                          href={p.href} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="text-sm font-black uppercase tracking-tight text-foreground hover:text-primary transition-colors flex items-center gap-3 group/ext"
                                        >
                                          <ChevronRight className="h-3 w-3 text-primary opacity-50 transition-transform group-hover/ext:translate-x-1" />
                                          {p.name}
                                          <ExternalLink className="h-3 w-3 opacity-0 group-hover/ext:opacity-100 transition-opacity ml-1" />
                                        </a>
                                      ) : (
                                        <Link to={p.href} className="text-sm font-black uppercase tracking-tight text-foreground hover:text-primary transition-colors flex items-center gap-3 group/ext">
                                          <ChevronRight className="h-3 w-3 text-primary opacity-50 transition-transform group-hover/ext:translate-x-1" />
                                          {p.name}
                                        </Link>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className="p-10 bg-secondary/30 border border-border rounded-2xl flex flex-col justify-center items-center text-center space-y-6">
                                <Users className="h-16 w-16 text-primary/10" />
                                <div className="space-y-2">
                                  <p className="text-[10px] font-black uppercase tracking-widest text-primary">{t('Ecossistema Global', 'Ecosistema Global', 'Global Ecosystem')}</p>
                                  <p className="text-xs text-muted-foreground leading-relaxed max-w-[240px] uppercase font-bold tracking-tight">
                                    {t('Conectamos tecnologias globais e presença regional para entregar soluções robustas.', 'Conectamos tecnologías globales y presencia regional para ofrecer soluciones sólidas.', 'We connect global technologies and regional presence to deliver robust solutions.')}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="pt-8 border-t border-border mt-auto">
                              <Link to="/parceiros" className="text-[10px] font-black uppercase tracking-widest text-primary hover:brightness-110 transition-all flex items-center gap-2">
                                <span>{t('Ver todos os parceiros', 'Ver todos los socios', 'See all partners')}</span>
                                <ArrowRight className="h-4 w-4" />
                              </Link>
                            </div>
                          </div>
                        )}

                        {navigation.find(n => n.name === activeMegamenu)?.megamenu?.type === 'press' && (
                          <div className="h-full flex flex-col pt-10">
                            <div className="grid grid-cols-2 gap-8 flex-1">
                              <div className="space-y-8">
                                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-primary/60 border-l-2 border-primary/20 pl-4">{t('Tópicos e Recursos', 'Temas y Recursos', 'Topics and Resources')}</h3>
                                <ul className="space-y-6">
                                  {getPressMegamenu(t).groups.find(g => g.id === activeSubItem)?.items.map(p => (
                                    <li key={p.name}>
                                      <Link to={p.href} className="text-sm font-black uppercase tracking-tight text-foreground hover:text-primary transition-colors flex items-center gap-3 group/ext">
                                        <ChevronRight className="h-3 w-3 text-primary opacity-50 transition-transform group-hover/ext:translate-x-1" />
                                        {p.name}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className="p-10 bg-secondary/30 border border-border rounded-2xl flex flex-col justify-center items-center text-center space-y-6">
                                <Newspaper className="h-16 w-16 text-primary/10" />
                                <div className="space-y-2">
                                  <p className="text-[10px] font-black uppercase tracking-widest text-primary">{t('Sala de Imprensa', 'Sala de Prensa', 'Newsroom')}</p>
                                  <p className="text-xs text-muted-foreground leading-relaxed max-w-[240px] uppercase font-bold tracking-tight">
                                    {t('Fique por dentro das novidades, eventos e estudos de caso da TechEnabler.', 'Manténgase informado sobre noticias, eventos y casos de estudio de TechEnabler.', 'Stay up to date with TechEnabler news, events and case studies.')}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="pt-8 border-t border-border mt-auto">
                              <Link to="/sala-de-imprensa" className="text-[10px] font-black uppercase tracking-widest text-primary hover:brightness-110 transition-all flex items-center gap-2">
                                <span>{t('Acessar Newsroom', 'Acceder a Newsroom', 'Go to Newsroom')}</span>
                                <ArrowRight className="h-4 w-4" />
                              </Link>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 lg:hidden"
          >
            <div className="fixed inset-0 bg-background/95 backdrop-blur-md" onClick={() => setMobileMenuOpen(false)} />
            <motion.div 
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-background px-4 sm:px-6 py-6 sm:max-w-sm border-l border-border"
            >
              <div className="flex items-center justify-between mb-8 sm:mb-12 gap-2">
                <Link to="/" className="-m-1.5 p-1.5 flex items-center gap-2 shrink-0 min-w-0" onClick={() => setMobileMenuOpen(false)}>
                  <img
                    src="/techenabler-logo-gradiente.webp"
                    alt="TechEnabler"
                    width={479}
                    height={120}
                    className="h-6 sm:h-8 w-auto block dark:hidden object-contain shrink-0"
                    referrerPolicy="no-referrer"
                  />
                  <img
                    src="/techenabler-logo-branco.webp"
                    alt="TechEnabler"
                    width={479}
                    height={120}
                    className="h-6 sm:h-8 w-auto hidden dark:block object-contain shrink-0"
                    referrerPolicy="no-referrer"
                  />
                </Link>
                <div className="flex items-center gap-1 sm:gap-2">
                  <div className="flex items-center gap-1 border-r border-border pr-2 sm:pr-3 mr-0.5 sm:mr-1">
                    <button
                      onClick={() => setLanguage('pt')}
                      className={cn(
                        "text-base transition-opacity hover:opacity-100",
                        language === 'pt' ? "opacity-100 grayscale-0" : "opacity-40 grayscale"
                      )}
                      title="Português"
                    >
                      🇧🇷
                    </button>
                    <button
                      onClick={() => setLanguage('es')}
                      className={cn(
                        "text-base transition-opacity hover:opacity-100",
                        language === 'es' ? "opacity-100 grayscale-0" : "opacity-40 grayscale"
                      )}
                      title="Español"
                    >
                      🇪🇸
                    </button>
                    <button
                      onClick={() => setLanguage('en')}
                      className={cn(
                        "text-base transition-opacity hover:opacity-100",
                        language === 'en' ? "opacity-100 grayscale-0" : "opacity-40 grayscale"
                      )}
                      title="English"
                    >
                      🇺🇸
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setSearchOpen(true);
                    }}
                    className="p-1.5 text-muted-foreground hover:text-primary transition-colors focus:outline-none"
                    aria-label="Pesquisar"
                  >
                    <SearchIcon className="h-5 w-5" />
                  </button>
                  <ThemeToggle />
                  <button
                    type="button"
                    className="-m-1 rounded-md p-1.5 text-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="sr-only">Fechar menu</span>
                    <X className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-8">
                {navigation.map((item) => (
                  <div key={item.name} className="space-y-4">
                    <Link
                      to={item.href}
                      className={cn(
                        "text-xl font-bold tracking-tight text-foreground block",
                        location.pathname === item.href && "text-primary"
                      )}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                    {item.megamenu && (
                      <div className="grid grid-cols-1 gap-6 pl-4 border-l border-border">
                        {(item.megamenu.type === 'partners' ? getPartnersMegamenu(t).groups : 
                          getPressMegamenu(t).groups).map((sub: any) => (
                          <div key={sub.id} className="space-y-4">
                            <div className="flex items-center gap-2">
                              <sub.icon className="h-4 w-4 text-primary" />
                              <p className="text-sm font-bold text-foreground">
                                {sub.title}
                              </p>
                            </div>
                            <div className="grid grid-cols-1 gap-2 pl-6">
                              {sub.items.map((link: any) => (
                                link.href.startsWith('http') ? (
                                  <a
                                    key={link.name}
                                    href={link.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-medium text-muted-foreground hover:text-primary flex items-center gap-2 py-1"
                                    onClick={() => setMobileMenuOpen(false)}
                                  >
                                    {link.name}
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                ) : (
                                  <Link
                                    key={link.name}
                                    to={link.href}
                                    className="text-xs font-medium text-muted-foreground hover:text-primary block py-1"
                                    onClick={() => setMobileMenuOpen(false)}
                                  >
                                    {link.name}
                                  </Link>
                                )
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <Search isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}

